import { MAX_ATTENDEES_FOR_AVAILABILITY, SERVER_CALL_TIMEOUT } from '../constants';
import createSonoraPerformanceDatapoint from '../dataLogger/createSonoraPerformanceDatapoint';
import { log3sSearchAction } from '../dataLogger/log3sInstrumentation';
import getDataProviderToSuggestionsDictionary from './getDataProviderToSuggestionsDictionary';
import getPreferredSettings from './getPreferredSettings';
import getSonoraDataProviders from './getSonoraDataProviders';
import { getRequiredAttendeesFromRecipients } from './meetingTimes/getAttendeesFromRecipients';
import { DataProviderType, InlineSearchSuggestion, UserDataSignal } from '@augloop-types/honeybee';
import { AddOperation, IOperation, Item, SignalOperation } from '@augloop/types-core';
import { AugloopSession, lazyDisposeAugLoopSession, lazyGetAugLoopSession } from 'augloop-service';
import { PluginViewStateAdapter } from 'owa-editor';
import { errorThatWillCauseAlert, trace } from 'owa-trace';
import { Suggestion } from 'owa-userdataservice';
import type { ISession } from '@augloop/runtime-client';
import {
    updateAllSuggestions,
    updateNetworkCallOnDispatch,
    updateNetworkCallOnCompletion,
    updateNetworkCallOnTimeout,
} from '../mutators/sonoraMutators';
import type { SonoraDataProviderConfig, SonoraViewState } from 'owa-editor-sonora-types';
import type { IEditor } from 'roosterjs-editor-types';
import { isFeatureEnabled } from 'owa-feature-flags';

export default class SonoraAugloopClient {
    private augLoopSession: ISession | undefined;
    private timeOfLastSignal: number | undefined;
    private augLoopTimeoutFunction: any;
    private dataProviders: DataProviderType[] | undefined;

    constructor(
        private editor: IEditor | undefined,
        private viewState: SonoraViewState,
        private readonly augloopSessionAdapter: PluginViewStateAdapter<AugloopSession>,
        private dataProviderConfig: SonoraDataProviderConfig,
        private loadTimeDp: string,
        private updateDp: string
    ) {
        this.augLoopTimeoutFunction = null;
        this.dataProviders = getSonoraDataProviders(this.dataProviderConfig);
    }

    public async initializeSuggestions(preloadSuggestions: boolean) {
        const getAugLoopSession = await lazyGetAugLoopSession.import();

        getAugLoopSession(this.augloopSessionAdapter().sessionId).then(augLoopSession => {
            if (augLoopSession && this.editor) {
                trace.info('[SonoraAugloopClient] AL session initialization succeeded');

                // Register annotation, send a warmup operation.
                this.augLoopSession = augLoopSession;
                this.activateAnnotation();
                this.warmupSession();

                // Send preload signal if requested.
                if (preloadSuggestions) {
                    this.sendSignalForSuggestions('');
                }
            } else {
                trace.warn('[SonoraAugloopClient] AL session initialization failed');
            }
        });
    }

    public sendSignalForSuggestions(queryString: string) {
        // Update network call so that we show the spinner UI.
        updateNetworkCallOnDispatch(this.viewState);

        const op = this.buildSignal(queryString, false /* isWarmup */);

        this.timeOfLastSignal = Date.now();

        // We might invoke this function after disposing.
        // Just return in that case.
        if (!this.augLoopSession) {
            return;
        }
        this.augLoopSession.submitOperation(op);

        if (this.augLoopTimeoutFunction) {
            clearTimeout(this.augLoopTimeoutFunction);
        }

        this.augLoopTimeoutFunction = setTimeout(() => {
            // We might invoke this function after disposing.
            // Just return in that case.
            if (!this.dataProviders) {
                return;
            }

            // If we hit this code, we are assuming that the signal we sent to AL is not getting
            // an annotation back, either due to no suggestions or because AL timed out.
            trace.info('[SonoraAugloopClient] AL timeout');
            this.logLatencyDatapoint(SERVER_CALL_TIMEOUT);
            updateNetworkCallOnTimeout(this.viewState, true /* isAL */);

            const dataProviderToSuggestionsDictionary = getDataProviderToSuggestionsDictionary(
                this.dataProviders,
                this.dataProviderConfig,
                []
            );

            updateAllSuggestions(
                this.viewState,
                dataProviderToSuggestionsDictionary,
                SERVER_CALL_TIMEOUT,
                this.updateDp
            );
        }, SERVER_CALL_TIMEOUT);
    }

    public onDispose() {
        if (this.augLoopSession) {
            lazyDisposeAugLoopSession.importAndExecute(this.augloopSessionAdapter().sessionId);
            this.augLoopSession = undefined;
        }

        if (this.augLoopTimeoutFunction) {
            clearTimeout(this.augLoopTimeoutFunction);
            this.augLoopTimeoutFunction = undefined;
        }

        this.dataProviders = undefined;
    }

    private warmupSession() {
        // Send a warmup operation to get the Honeybee workflow going.
        const op = this.buildSignal('' /* queryString */, true /* isWarmup */);

        this.augLoopSession!.submitOperation(op);
    }

    private activateAnnotation() {
        const annotationType = InlineSearchSuggestion.getTypeName();

        // Activate the AugLoop_Honeybee_InlineSearchSuggestion suggestion and register
        // a callback to process it.
        let activationPromise = this.augLoopSession!.activateAnnotation(annotationType, {
            callback: this.onAnnotationResult,
        });

        activationPromise.catch(error => {
            trace.warn(
                `[SonoraAugloopClient] Exception encountered when activating ${annotationType} annotation: ${error.message}`
            );
        });
    }

    private onAnnotationResult = (operation: IOperation): void => {
        // If we disposed, just return.
        if (!this.dataProviders) {
            return;
        }

        const body = operation.items[0].body as any;
        const latestNetworkCallId = this.generateContextString();

        try {
            if (body!.context === latestNetworkCallId) {
                if (this.augLoopTimeoutFunction) {
                    clearTimeout(this.augLoopTimeoutFunction);
                }

                const latency = this.timeOfLastSignal ? Date.now() - this.timeOfLastSignal : 0;
                this.logLatencyDatapoint(latency);

                const results = body!.ad;

                // Convert the AL results into Suggestion objects, map the data providers
                // to their suggestions, and then update the viewState.
                const suggestions = results.map(result => {
                    return {
                        encodedJsonData: result.encodedJsonData,
                        sourceInfo: result.sourceInfo,
                        traceId: result.traceId,
                        logicalId: result.logicalId,
                    } as Suggestion;
                });

                const dataProviderToSuggestionsDictionary = getDataProviderToSuggestionsDictionary(
                    this.dataProviders!,
                    this.dataProviderConfig,
                    suggestions
                );

                updateNetworkCallOnCompletion(this.viewState);
                updateAllSuggestions(
                    this.viewState,
                    dataProviderToSuggestionsDictionary,
                    latency,
                    this.updateDp
                );
            }
        } catch (e) {
            errorThatWillCauseAlert(
                `[SonoraAugloopClient] Failed to parse annotation: ${e.message}`
            );
        }
    };

    private generateContextString(): string {
        return `sonoraOp${this.viewState.latestNetworkCallNumber}`;
    }

    private buildSignal(queryString: string, isWarmup: boolean) {
        const contextString = isWarmup ? 'warmup' : this.generateContextString();

        const signal = new UserDataSignal({
            text: queryString,
            dataProviders: this.dataProviders,
            customSettings: getPreferredSettings(
                isWarmup,
                JSON.stringify(
                    getRequiredAttendeesFromRecipients(
                        this.viewState.recipients,
                        MAX_ATTENDEES_FOR_AVAILABILITY,
                        true /* shouldKeepInternalAttendeesOnly */
                    )
                )
            ),
            context: contextString,
        });

        const item: Item = {
            id: contextString,
            body: signal,
        };

        const opData = {
            parentPath: ['Signal', UserDataSignal.getTypeName()],
            items: [item],
        };

        return isWarmup ? new AddOperation(opData) : new SignalOperation(opData);
    }

    private logLatencyDatapoint(latencyInMs: number) {
        // Keep consistent with same datapoint in fetchSuggestionsOrchestrator.ts.
        const performanceDatapoint = createSonoraPerformanceDatapoint(this.loadTimeDp, {
            isEmptyQuery: this.viewState.queryString.length === 0,
            dataSource: this.dataProviders!.map(type => DataProviderType[type]).toString(),
            isAL: true,
            workflow: isFeatureEnabled('honeybee-substrate-platform-integration')
                ? 'substrate'
                : 'legacy',
        });

        performanceDatapoint.end(latencyInMs);
        log3sSearchAction(this.viewState.results, 'searchdone');
    }
}
